import { useSearchParams, Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { Printer, ArrowLeft } from "lucide-react";

const RED = "#D32F2F";
const INK = "#191614";

const ASSET_TITLES = {
  onepager: "Overview one-pager",
  deck: "Board presentation",
  flyer: "Resident opt-in flyer",
  poster: "QR opt-in poster",
  card: "“Text us” welcome card",
  announcement: "Launch announcement (copy)",
  checklist: "Onboarding checklist",
  faq: "FAQ & objection handling",
  tech: "Technology & architecture",
  roi: "Portfolio ROI estimate",
  yardsign: "Yard / entrance sign",
  amenitysign: "Amenity sign (pool · gym · clubhouse)",
  windowsign: "Office window sign",
  tabletent: "Table tent",
  doorhanger: "Door hanger",
};

// Management-company variants of the persona-specific assets (different audience + messaging).
const ASSET_TITLES_COMPANY = {
  onepager: "Overview one-pager (management company)",
  deck: "Portfolio presentation",
  faq: "FAQ & objection handling (management company)",
  checklist: "Portfolio rollout checklist",
};

const Logo = ({ variant = "red", className = "" }) => (
  <img src={`/brand/redline-${variant}.png`} alt="Ask Redline" className={className} />
);

const Sheet = ({ children, className = "", ...rest }) => (
  <div className={`sheet mx-auto bg-white text-ink ${className}`} {...rest}>{children}</div>
);

// ── Individual assets ────────────────────────────────────────────────────────
function OnePager({ name, phone, link, voice = true }) {
  const benefits = [
    ["24/7 instant answers", "Residents get accurate answers about rules, amenities, hours, fees and events — any time, from your governing documents. No more waiting for office hours."],
    ["Fewer calls & emails", "The concierge handles the repetitive questions that flood your manager's inbox, so your team focuses on the work that actually needs a human."],
    ["Requests & bookings, handled", "Residents log maintenance requests, reserve amenities, RSVP to events and answer surveys — all by text or phone, logged automatically."],
    ["Every word on the record", "Complete, searchable transcripts of every conversation, with the source behind each answer — a clean audit trail for the board."],
  ];
  return (
    <Sheet className="p-[0.6in]">
      <div className="flex items-center justify-between border-b-2 pb-4" style={{ borderColor: RED }}>
        <Logo variant="red" className="h-9" />
        <div className="text-right text-[11px] uppercase tracking-[0.2em] font-bold" style={{ color: RED }}>AI Resident Concierge</div>
      </div>
      <h1 className="mt-8 text-[34px] leading-[1.1] font-extrabold" style={{ fontFamily: "Poppins, sans-serif" }}>
        Your community's questions,<br />answered in seconds.
      </h1>
      <p className="mt-4 text-[15px] text-ink-600 max-w-[6.2in]">
        Ask Redline is an AI resident concierge for HOAs. Residents text or call one number and get
        instant, accurate answers — while your manager finally gets their day back.
      </p>
      <div className="mt-8 grid grid-cols-2 gap-5">
        {benefits.map(([h, d]) => (
          <div key={h} className="rounded-2xl border border-sand-200 p-5">
            <div className="text-[15px] font-extrabold" style={{ color: RED }}>{h}</div>
            <p className="mt-1.5 text-[12.5px] leading-relaxed text-ink-600">{d}</p>
          </div>
        ))}
      </div>
      <div className="mt-8 rounded-2xl p-6 text-white" style={{ background: INK }}>
        <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-sand-300">How it works</div>
        <div className="mt-3 grid grid-cols-3 gap-4 text-[12.5px]">
          <div><div className="font-extrabold text-[15px]" style={{ color: "#F4A3A3" }}>1. We learn your community</div><p className="mt-1 text-sand-100/90">Your CC&Rs, rules, hours, fees, contacts and FAQs are loaded into the concierge.</p></div>
          <div><div className="font-extrabold text-[15px]" style={{ color: "#F4A3A3" }}>2. Residents reach out</div><p className="mt-1 text-sand-100/90">They text or call {name}'s number — no app to download, no login.</p></div>
          <div><div className="font-extrabold text-[15px]" style={{ color: "#F4A3A3" }}>3. It answers & escalates</div><p className="mt-1 text-sand-100/90">Instant answers, logged requests, and a clean handoff to your team when a human is needed.</p></div>
        </div>
      </div>
      <div className="mt-8 flex items-center justify-between text-[12px] text-ink-600">
        <div>{voice ? "Text or call" : "Text"} the concierge: <span className="font-bold text-ink">{phone}</span></div>
        <div>{link}</div>
      </div>
    </Sheet>
  );
}

function Deck({ name, phone, keyword, voice = true }) {
  const slide = (n, kicker, title, body) => (
    <Sheet className="p-[0.7in] flex flex-col justify-center min-h-[7.5in] break-after-page">
      <div className="flex items-center justify-between">
        <Logo variant="red" className="h-7" />
        <div className="text-[11px] text-ink/40 font-mono">{n} / 8</div>
      </div>
      <div className="my-auto py-10">
        {kicker && <div className="text-[12px] uppercase tracking-[0.2em] font-bold" style={{ color: RED }}>{kicker}</div>}
        <h2 className="mt-3 text-[30px] leading-tight font-extrabold" style={{ fontFamily: "Poppins, sans-serif" }}>{title}</h2>
        <div className="mt-5 text-[15px] leading-relaxed text-ink-600 space-y-2">{body}</div>
      </div>
    </Sheet>
  );
  const li = (items) => <ul className="space-y-2">{items.map((t, i) => <li key={i} className="flex gap-2"><span style={{ color: RED }} className="font-bold">▸</span><span>{t}</span></li>)}</ul>;
  return (
    <>
      <Sheet className="p-[0.7in] flex flex-col justify-center min-h-[7.5in] break-after-page text-white" style={{ background: INK }}>
        <Logo variant="white" className="h-9 self-start" />
        <div className="my-auto">
          <div className="text-[12px] uppercase tracking-[0.25em] font-bold" style={{ color: "#F4A3A3" }}>AI Resident Concierge</div>
          <h1 className="mt-4 text-[40px] leading-[1.05] font-extrabold" style={{ fontFamily: "Poppins, sans-serif" }}>Bringing Ask Redline<br />to {name}</h1>
          <p className="mt-5 text-[15px] text-sand-100/80 max-w-[6in]">A proposal for the board — instant resident answers by text and phone, 24/7.</p>
        </div>
        <div className="text-[12px] text-sand-300">Presented by your Ask Redline onboarding team</div>
      </Sheet>
      {slide(2, "The problem", "Your manager is a switchboard.", <p>The same questions — pool hours, gate codes, trash day, how to pay dues, is my payment in — arrive by phone and email all day. They interrupt real work, pile up after hours, and every resident feels like a low priority.</p>)}
      {slide(3, "The solution", "One number. Instant answers. Any hour.", <p>Ask Redline is an AI concierge trained on <b>your</b> governing documents, rules and contacts. Residents simply text or call — and get an accurate answer in seconds, 24/7, in plain language.</p>)}
      {slide(4, "For residents", "Everything they need, by text or call", li(["Answers about rules, amenities, hours, fees and events", "Log a maintenance request (with a photo)", "Reserve the clubhouse, pool or courts", "RSVP to community events and vote in surveys", "Reach a real person the moment it matters"]))}
      {slide(5, "For the board & manager", "Control, visibility, and hours back", li(["A dashboard of every conversation with the source behind each answer", "Automatic escalation when a human is needed — nothing slips", "Requests, bookings and RSVPs logged for you", "Analytics on what residents actually ask", "Broadcast announcements by text in one click"]))}
      {slide(6, "Privacy & control", "Safe by design", li(["Community-specific info is shared only with verified residents", "Full SMS consent management (opt-in / STOP honored)", "The AI hands off to your team — it never pretends to be one", "You decide what it can do and what stays read-only"]))}
      {slide(7, "Rollout", "Live in about two weeks", li(["We load your documents, rules, hours, fees and contacts", "You approve the concierge's tone and what it's allowed to do", "We invite residents to opt in (flyer, QR poster, welcome text)", `Residents text ${phone}${voice ? " (or call it)" : ""} or reply ${keyword} to get started`]))}
      {slide(8, "Next steps", "Let's get your community set up.", <p>We'll handle the heavy lifting. All we need from the board is a green light and your current documents. <span className="font-bold" style={{ color: RED }}>Text {phone} to see it in action right now.</span></p>)}
    </>
  );
}

function Flyer({ name, phone, link, keyword, voice = true }) {
  return (
    <Sheet className="p-[0.7in] min-h-[9.5in] flex flex-col">
      <Logo variant="red" className="h-8 self-center" />
      <div className="mt-8 text-center">
        <div className="text-[13px] uppercase tracking-[0.25em] font-bold" style={{ color: RED }}>Now serving {name}</div>
        <h1 className="mt-3 text-[40px] leading-[1.05] font-extrabold" style={{ fontFamily: "Poppins, sans-serif" }}>Meet your community's<br />{voice ? "24/7 concierge." : "text assistant."}</h1>
        <p className="mt-4 text-[16px] text-ink-600 max-w-[5.5in] mx-auto">Pool hours? Gate code? Trash day? A maintenance issue? Just text{voice ? " or call" : ""} — and get an answer in seconds, any time of day.</p>
      </div>
      <div className="mt-8 flex items-center justify-center gap-8">
        <div className="rounded-3xl border-4 p-4" style={{ borderColor: INK }}>
          <QRCodeSVG value={link} size={190} fgColor={INK} level="M" />
        </div>
        <div className="text-left">
          <div className="text-[12px] uppercase tracking-[0.18em] font-bold text-ink/50">Two ways to start</div>
          <div className="mt-3 text-[18px] font-extrabold">1 &nbsp;Scan the code</div>
          <p className="text-[13px] text-ink-600">Point your phone camera at the QR to opt in.</p>
          <div className="mt-4 text-[18px] font-extrabold">2 &nbsp;Or text us</div>
          <p className="text-[13px] text-ink-600">Send <span className="font-bold" style={{ color: RED }}>{keyword}</span> to <span className="font-bold">{phone}</span></p>
          {voice && <p className="mt-3 text-[13px] text-ink-600"><span className="font-extrabold">Prefer to talk?</span> Just call <span className="font-bold">{phone}</span>.</p>}
        </div>
      </div>
      <div className="mt-auto pt-8 text-center text-[10.5px] text-ink/45 max-w-[6in] mx-auto">
        By opting in you agree to receive community text messages. Message &amp; data rates may apply. Reply STOP to opt out, HELP for help. Consent is not a condition of purchase.
      </div>
    </Sheet>
  );
}

function Poster({ name, phone, link, keyword, voice = true }) {
  return (
    <Sheet className="p-[0.7in] min-h-[9.8in] flex flex-col items-center justify-center text-center">
      <Logo variant="red" className="h-10" />
      <h1 className="mt-10 text-[52px] leading-[1.02] font-extrabold" style={{ fontFamily: "Poppins, sans-serif" }}>Questions about<br />the community?</h1>
      <div className="mt-4 text-[26px] font-extrabold" style={{ color: RED }}>{voice ? "Text or call us." : "Just text us."}</div>
      <div className="mt-8 rounded-3xl border-4 p-5" style={{ borderColor: INK }}>
        <QRCodeSVG value={link} size={260} fgColor={INK} level="M" />
      </div>
      <div className="mt-6 text-[18px]">Scan to opt in — or text <span className="font-extrabold">{keyword}</span> to <span className="font-extrabold">{phone}</span>{voice ? " — or just call it" : ""}</div>
      <div className="mt-2 text-[13px] text-ink/50">{name} · powered by Ask Redline</div>
      <div className="mt-6 text-[10px] text-ink/40 max-w-[6in]">Msg &amp; data rates may apply. Reply STOP to opt out, HELP for help.</div>
    </Sheet>
  );
}

function WelcomeCard({ name, phone, link, keyword, voice = true }) {
  return (
    <Sheet className="p-[0.5in]">
      <div className="text-[11px] text-ink/40 mb-2 print:hidden">Prints two cards — cut along the dividers.</div>
      {[0, 1].map((i) => (
        <div key={i} className="rounded-2xl border-2 border-dashed border-sand-300 p-6 flex items-center gap-6 mb-5" style={{ minHeight: "3.1in" }}>
          <div className="rounded-2xl border-2 p-2 shrink-0" style={{ borderColor: INK }}>
            <QRCodeSVG value={link} size={120} fgColor={INK} level="M" />
          </div>
          <div>
            <Logo variant="red" className="h-6" />
            <div className="mt-2 text-[22px] font-extrabold leading-tight" style={{ fontFamily: "Poppins, sans-serif" }}>Welcome to {name}!</div>
            <p className="mt-1 text-[13px] text-ink-600">Save this number — your 24/7 community concierge. Ask about rules, amenities, maintenance and more, any time.</p>
            <div className="mt-2 text-[15px] font-extrabold">Text {keyword} to {phone}{voice ? " · or call it" : ""}</div>
          </div>
        </div>
      ))}
    </Sheet>
  );
}

function Announcement({ name, phone, keyword, voice = true }) {
  const block = (label, text) => (
    <div className="rounded-2xl border border-sand-200 overflow-hidden mb-6">
      <div className="px-5 py-2.5 text-[11px] font-bold uppercase tracking-[0.15em] text-white" style={{ background: INK }}>{label}</div>
      <pre className="px-5 py-4 text-[13px] leading-relaxed whitespace-pre-wrap font-sans text-ink-700">{text}</pre>
    </div>
  );
  return (
    <Sheet className="p-[0.6in]">
      <div className="flex items-center gap-3 border-b-2 pb-4" style={{ borderColor: RED }}>
        <Logo variant="red" className="h-8" /><div className="text-[15px] font-extrabold">Launch announcement — ready to copy</div>
      </div>
      <p className="mt-4 text-[12.5px] text-ink-600">Send these when {name} goes live. Personalize the bracketed bits before sending.</p>
      <div className="mt-5">
        {block("Email to residents", `Subject: ${name} now has a 24/7 concierge${voice ? " — text or call" : ""}\n\nHi neighbors,\n\nWe're excited to introduce ${name}'s new AI concierge — a simple way to get answers about our community any time of day. ${voice ? "Text or call" : "Text"} a single number with questions about pool hours, rules, gate codes, trash day, maintenance and more, and get an instant answer.\n\nGetting started takes ten seconds:\n  • Text ${keyword} to ${phone}, or scan the QR code on the flyer in the mailroom.${voice ? `\n  • Prefer to talk? Just call ${phone} — day or night.` : ""}\n  • Ask anything — day or night.\n\nA real person is always just a message away when you need one. We hope this makes life here a little easier.\n\n— The Board & Management, ${name}`)}
        {block("Text / SMS blast", `${name}: Meet your new 24/7 community concierge! ${voice ? "Text or call" : "Text"} for answers about amenities, rules & maintenance anytime. Reply ${keyword} to opt in. Msg&data rates may apply; reply STOP to opt out.`)}
      </div>
    </Sheet>
  );
}

function Checklist({ name }) {
  const phase = (title, sub, items) => (
    <div className="mb-6">
      <div className="text-[15px] font-extrabold" style={{ color: RED }}>{title} <span className="text-ink/40 font-semibold text-[12px]">· {sub}</span></div>
      <ul className="mt-2 space-y-1.5">{items.map((t, i) => <li key={i} className="flex gap-2.5 text-[13px]"><span className="mt-0.5 inline-block h-3.5 w-3.5 rounded border-2 border-sand-400 shrink-0" />{t}</li>)}</ul>
    </div>
  );
  return (
    <Sheet className="p-[0.6in]">
      <div className="flex items-center justify-between border-b-2 pb-4" style={{ borderColor: RED }}>
        <Logo variant="red" className="h-8" /><div className="text-[15px] font-extrabold">Onboarding checklist — {name}</div>
      </div>
      <p className="mt-4 text-[12.5px] text-ink-600">A simple playbook to take {name} from kickoff to a confident, well-used concierge in the first 90 days.</p>
      <div className="mt-5">
        {phase("Pre-launch", "Week 0", ["Collect CC&Rs, bylaws, rules & regs, architectural guidelines", "Gather hours, fees, gate/pool codes policy, trash schedule, key contacts & vendors", "Confirm the community phone number and opt-in keyword", "Load documents; tag contacts by type (utility vs repair vendor) and topic", "Set the concierge's tone and what it's allowed to do (bookings, requests, profile edits)", "Review a test conversation with the board/manager"])}
        {phase("Launch", "Week 1", ["Print the opt-in flyer, QR poster (mailroom/clubhouse) and welcome cards", "Send the launch email + SMS announcement to residents", "Post the QR poster in high-traffic spots", "Monitor the first conversations daily; fix any gaps with Quick Facts"])}
        {phase("Adopt", "Weeks 2–4", ["Review low-confidence / unanswered questions and add facts or docs", "Confirm escalations are routing to the right person", "Share early wins with the board (volume handled, response time)", "Add any missing vendors/contacts residents asked about"])}
        {phase("Optimize", "Days 30–90", ["Review analytics: top topics, CSAT, escalation rate", "Tune restricted actions and account-standing rules if needed", "Enable additional modules (amenities, surveys, broadcasts) as adoption grows", "Schedule a 90-day board review of results"])}
      </div>
    </Sheet>
  );
}

function Faq() {
  const qa = [
    ["Does this replace our manager?", "No — it removes the repetitive questions so your manager can focus on real work. Anything that needs a human is escalated instantly, and residents can always reach a person."],
    ["Is it accurate? What if it doesn't know?", "It answers only from your community's documents and approved facts — it doesn't guess. When it's unsure, it says so and hands off to your team rather than making something up."],
    ["What about residents who don't text?", "The concierge also answers phone calls, and the resident portal and your office stay exactly as they are. Nobody is forced to use it."],
    ["Is resident information secure?", "Community-specific details are shared only with verified residents, and every conversation is logged. Consent is fully managed — residents opt in and can text STOP any time."],
    ["Will it text residents without permission?", "No. It only messages residents who have opted in, and it honors STOP immediately. It's built around TCPA-style consent."],
    ["How much work is it for the board?", "Very little. We load your documents and set it up; you approve the tone and permissions. Most communities are live in about two weeks."],
    ["What does it cost?", "Your onboarding rep will put a proposal together for your community and walk you through it."],
    ["What if we want to turn something off?", "You're in control. Every capability — bookings, requests, profile changes — can be switched on or off, and set to read-only, per community."],
  ];
  return (
    <Sheet className="p-[0.6in]">
      <div className="flex items-center justify-between border-b-2 pb-4" style={{ borderColor: RED }}>
        <Logo variant="red" className="h-8" /><div className="text-[15px] font-extrabold">FAQ &amp; objection handling</div>
      </div>
      <div className="mt-5 grid grid-cols-1 gap-4">
        {qa.map(([q, a]) => (
          <div key={q}>
            <div className="text-[13.5px] font-extrabold text-ink">{q}</div>
            <p className="mt-1 text-[12.5px] leading-relaxed text-ink-600">{a}</p>
          </div>
        ))}
      </div>
    </Sheet>
  );
}

function TechSheet() {
  const card = (title, items) => (
    <div className="rounded-2xl border border-sand-200 p-5">
      <div className="text-[14px] font-extrabold" style={{ color: RED }}>{title}</div>
      <ul className="mt-2 space-y-1.5">
        {items.map((t, i) => (
          <li key={i} className="flex gap-2 text-[12px] leading-relaxed text-ink-600">
            <span style={{ color: RED }} className="font-bold">▸</span><span>{t}</span>
          </li>
        ))}
      </ul>
    </div>
  );
  const step = (n, title, body) => (
    <div>
      <div className="font-extrabold text-[14px]" style={{ color: "#F4A3A3" }}>{n}. {title}</div>
      <p className="mt-1 text-[11.5px] leading-relaxed text-sand-100/90">{body}</p>
    </div>
  );
  const stat = (big, small) => (
    <div className="rounded-2xl border border-sand-200 px-4 py-3 text-center">
      <div className="text-[20px] font-extrabold leading-none" style={{ color: RED, fontFamily: "Poppins, sans-serif" }}>{big}</div>
      <div className="mt-1 text-[10.5px] leading-snug text-ink-600">{small}</div>
    </div>
  );
  return (
    <Sheet className="p-[0.6in]">
      <div className="flex items-center justify-between border-b-2 pb-4" style={{ borderColor: RED }}>
        <Logo variant="red" className="h-9" />
        <div className="text-right text-[11px] uppercase tracking-[0.2em] font-bold" style={{ color: RED }}>Technology &amp; Architecture</div>
      </div>

      <h1 className="mt-7 text-[32px] leading-[1.08] font-extrabold" style={{ fontFamily: "Poppins, sans-serif" }}>
        Built to enterprise standard.
      </h1>
      <p className="mt-3 text-[13px] leading-relaxed text-ink-600 max-w-[6.4in]">
        Ask Redline is a modern, cloud-native platform — a purpose-built AI concierge, not a chatbot bolted onto a
        website. Here is what runs under the hood, how an answer is produced, and how residents&rsquo; data stays safe.
      </p>

      {/* Architecture flow */}
      <div className="mt-6 rounded-2xl p-6 text-white" style={{ background: INK }}>
        <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-sand-300">How an answer is produced</div>
        <div className="mt-3 grid grid-cols-4 gap-4">
          {step(1, "Resident reaches out", "They text, WhatsApp or call one number — no app, no login.")}
          {step(2, "The brain retrieves", "Vector search pulls the exact passages from your governing documents (RAG).")}
          {step(3, "Grounded reply", "The AI answers in plain language and cites the source — or logs a request.")}
          {step(4, "Human when needed", "It escalates to your team; managers &amp; board see everything in a live dashboard.")}
        </div>
      </div>

      {/* Stack grid */}
      <div className="mt-6 grid grid-cols-2 gap-4">
        {card("Web application", [
          "React 19 single-page app",
          "Tailwind CSS + accessible component library",
          "Real-time dashboards for managers & board",
        ])}
        {card("Backend & API", [
          "Python FastAPI — fully async, 50+ service modules",
          "Real-time voice bridge (WebSockets) for phone calls",
          "Background schedulers for reports, alerts & broadcasts",
        ])}
        {card("Data & AI", [
          "MongoDB Atlas with Vector Search for retrieval",
          "Anthropic Claude + OpenAI Realtime & Whisper (speech)",
          "Answers grounded in YOUR documents, with citations",
        ])}
        {card("Integrations & security", [
          "Twilio (SMS / WhatsApp / voice) · Resend (email)",
          "Google sign-in · role-based access control",
          "Multi-tenant isolation · full SMS consent (opt-in / STOP)",
        ])}
      </div>

      {/* By the numbers */}
      <div className="mt-6">
        <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-ink/40">By the numbers</div>
        <div className="mt-3 grid grid-cols-4 gap-3">
          {stat("70,000+", "lines of purpose-built code")}
          {stat("1 : 1", "secure isolated workspace per community")}
          {stat("100%", "of answers traceable to a source document")}
          {stat("24 / 7", "text & phone coverage, every day")}
        </div>
      </div>

      <div className="mt-6 flex items-center justify-between text-[11px] text-ink/50">
        <div>Enterprise data store · Atlas Vector Search · TCPA-style consent by design</div>
        <div className="font-bold" style={{ color: RED }}>askredline.com</div>
      </div>
    </Sheet>
  );
}

// ── Management-company variants ──────────────────────────────────────────────
const firmOf = (name) => (name && name !== "your community" ? name : "your firm");

function OnePagerCompany({ name, phone, link }) {
  const firm = firmOf(name);
  const benefits = [
    ["Cut your cost-to-serve", "Deflect the repetitive calls and emails that eat your team's day — across every community you manage. Fewer touches per property means more properties per manager."],
    ["Win & keep contracts", "Walk into every board interview with a 24/7 AI concierge legacy competitors can't match, and give existing boards a concrete reason to renew every year."],
    ["Standardize service quality", "Every community gets the same instant, accurate answers from its own governing documents — consistent, auditable service you can guarantee in an RFP."],
    ["Run it all from one place", "A portfolio dashboard and unified inbox across every community, with per-community white-labeling and role-based access for your community managers."],
  ];
  return (
    <Sheet className="p-[0.6in]">
      <div className="flex items-center justify-between border-b-2 pb-4" style={{ borderColor: RED }}>
        <Logo variant="red" className="h-9" />
        <div className="text-right text-[11px] uppercase tracking-[0.2em] font-bold" style={{ color: RED }}>For management companies</div>
      </div>
      <h1 className="mt-8 text-[34px] leading-[1.1] font-extrabold" style={{ fontFamily: "Poppins, sans-serif" }}>
        One concierge for every<br />community you run.
      </h1>
      <p className="mt-4 text-[15px] text-ink-600 max-w-[6.2in]">
        Ask Redline is an AI resident concierge built for management companies. Residents across your whole
        portfolio text or call one number per community and get instant, accurate answers, while your team&rsquo;s
        phones finally go quiet and your margin improves on every door.
      </p>
      <div className="mt-8 grid grid-cols-2 gap-5">
        {benefits.map(([h, d]) => (
          <div key={h} className="rounded-2xl border border-sand-200 p-5">
            <div className="text-[15px] font-extrabold" style={{ color: RED }}>{h}</div>
            <p className="mt-1.5 text-[12.5px] leading-relaxed text-ink-600">{d}</p>
          </div>
        ))}
      </div>
      <div className="mt-8 rounded-2xl p-6 text-white" style={{ background: INK }}>
        <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-sand-300">How it works across your portfolio</div>
        <div className="mt-3 grid grid-cols-3 gap-4 text-[12.5px]">
          <div><div className="font-extrabold text-[15px]" style={{ color: "#F4A3A3" }}>1. We onboard the portfolio</div><p className="mt-1 text-sand-100/90">Each community&rsquo;s CC&amp;Rs, rules, hours, fees and contacts load into its own concierge.</p></div>
          <div><div className="font-extrabold text-[15px]" style={{ color: "#F4A3A3" }}>2. Residents reach their community</div><p className="mt-1 text-sand-100/90">They text or call their community&rsquo;s number — no app, no login, fully branded.</p></div>
          <div><div className="font-extrabold text-[15px]" style={{ color: "#F4A3A3" }}>3. It answers &amp; routes</div><p className="mt-1 text-sand-100/90">Instant answers, logged requests, clean escalation to the right manager — all in one dashboard.</p></div>
        </div>
      </div>
      <div className="mt-8 flex items-center justify-between text-[12px] text-ink-600">
        <div>A proposal for <span className="font-bold text-ink">{firm}</span></div>
        <div>{link}</div>
      </div>
    </Sheet>
  );
}

function DeckCompany({ name, phone }) {
  const firm = firmOf(name);
  const slide = (n, kicker, title, body) => (
    <Sheet className="p-[0.7in] flex flex-col justify-center min-h-[7.5in] break-after-page">
      <div className="flex items-center justify-between">
        <Logo variant="red" className="h-7" />
        <div className="text-[11px] text-ink/40 font-mono">{n} / 8</div>
      </div>
      <div className="my-auto py-10">
        {kicker && <div className="text-[12px] uppercase tracking-[0.2em] font-bold" style={{ color: RED }}>{kicker}</div>}
        <h2 className="mt-3 text-[30px] leading-tight font-extrabold" style={{ fontFamily: "Poppins, sans-serif" }}>{title}</h2>
        <div className="mt-5 text-[15px] leading-relaxed text-ink-600 space-y-2">{body}</div>
      </div>
    </Sheet>
  );
  const li = (items) => <ul className="space-y-2">{items.map((t, i) => <li key={i} className="flex gap-2"><span style={{ color: RED }} className="font-bold">▸</span><span>{t}</span></li>)}</ul>;
  return (
    <>
      <Sheet className="p-[0.7in] flex flex-col justify-center min-h-[7.5in] break-after-page text-white" style={{ background: INK }}>
        <Logo variant="white" className="h-9 self-start" />
        <div className="my-auto">
          <div className="text-[12px] uppercase tracking-[0.25em] font-bold" style={{ color: "#F4A3A3" }}>AI Resident Concierge · For management companies</div>
          <h1 className="mt-4 text-[38px] leading-[1.05] font-extrabold" style={{ fontFamily: "Poppins, sans-serif" }}>An AI concierge for every<br />community {firm} runs</h1>
          <p className="mt-5 text-[15px] text-sand-100/80 max-w-[6in]">A proposal for leadership: cut cost-to-serve, standardize service, and win more board contracts across the portfolio.</p>
        </div>
        <div className="text-[12px] text-sand-300">Presented by your Ask Redline team</div>
      </Sheet>
      {slide(2, "The problem", "Your teams are drowning in repetitive calls.", <p>Across every community you manage, the same questions — hours, gate codes, dues, trash day — flood your managers all day. Labor is your biggest cost, call volume grows with every door you add, and service quality slips when staff are stretched thin.</p>)}
      {slide(3, "The opportunity", "Answer every resident, in every community, instantly.", <p>Ask Redline is an AI concierge trained per community on its own governing documents. Residents text or call their community&rsquo;s number and get an accurate answer in seconds, 24/7 — with no new headcount.</p>)}
      {slide(4, "The business case", "Protect margin. Win contracts.", li(["Deflect a large share of inbound calls and emails, lowering cost-to-serve on every door", "A 24/7 concierge is a concrete differentiator in board interviews and at renewal", "Standardized, auditable service quality across the entire portfolio", "Roll out to a new community in about two weeks — a value-add you can price in"]))}
      {slide(5, "For your managers", "Less busywork, more portfolio per person.", li(["The repetitive Q&A is handled automatically, day and night", "Requests, bookings and RSVPs are logged for you", "Clean escalation routes each issue to the right manager", "One unified inbox and dashboard across every community"]))}
      {slide(6, "For your boards & residents", "A visible service upgrade they feel.", li(["Instant answers by text or phone, any hour", "Per-community branding — it feels like their community's own", "Full transcripts and analytics for each board", "Privacy and SMS consent handled by design"]))}
      {slide(7, "Rollout & control", "Onboard the portfolio, community by community.", li(["We load each community's documents, rules, hours and contacts", "You set the tone and permissions per community", "Role-based access scopes community managers to their communities", "Add new communities anytime — the playbook is repeatable"]))}
      {slide(8, "Next steps", "Let's model the ROI for your portfolio.", <p>We&rsquo;ll estimate call deflection and cost-to-serve savings across your door count, and set up a pilot in one or two communities. <span className="font-bold" style={{ color: RED }}>Text {phone} to see the concierge in action right now.</span></p>)}
    </>
  );
}

function FaqCompany() {
  const qa = [
    ["How is this different from a resident portal we already offer?", "Portals are passive — residents still call when they can't find something. Ask Redline actively answers by text and phone from each community's documents and escalates only when a human is needed. It removes calls, it doesn't just add another login."],
    ["Does it replace our community managers?", "No. It removes the repetitive volume so each manager can handle more communities well. Anything needing judgment is escalated instantly to the right person on your team."],
    ["How does it work across a portfolio?", "One subscription per community with portfolio-friendly terms, plus optional add-on modules (voice, field services, broadcasts, amenities). Your rep will model it against your door count and expected call deflection."],
    ["How fast can we roll out across many communities?", "About two weeks per community, and communities onboard in parallel waves. We handle document loading; you approve tone and permissions once and reuse them."],
    ["Is each community's data isolated?", "Yes. Every community is a separate tenant — its documents and residents are walled off from the others. Role-based access lets community managers see only their assigned communities."],
    ["Is it secure and compliant?", "An enterprise cloud data store, encrypted transport, full SMS consent (opt-in / STOP, TCPA-style) and a complete audit trail of every conversation and the source behind each answer."],
    ["Will it actually help us win and keep contracts?", "Yes — it's a concrete, 24/7 differentiator in board interviews and a retention lever at renewal. Boards feel the service upgrade immediately, and you can point to volume handled and response times."],
    ["What if a specific community wants something turned off?", "Every capability is configurable per community and can be set read-only, so you standardize where you want efficiency and customize where a board requires it."],
  ];
  return (
    <Sheet className="p-[0.6in]">
      <div className="flex items-center justify-between border-b-2 pb-4" style={{ borderColor: RED }}>
        <Logo variant="red" className="h-8" /><div className="text-[15px] font-extrabold">FAQ &amp; objection handling — management companies</div>
      </div>
      <div className="mt-5 grid grid-cols-1 gap-4">
        {qa.map(([q, a]) => (
          <div key={q}>
            <div className="text-[13.5px] font-extrabold text-ink">{q}</div>
            <p className="mt-1 text-[12.5px] leading-relaxed text-ink-600">{a}</p>
          </div>
        ))}
      </div>
    </Sheet>
  );
}

function ChecklistCompany({ name }) {
  const firm = firmOf(name);
  const phase = (title, sub, items) => (
    <div className="mb-6">
      <div className="text-[15px] font-extrabold" style={{ color: RED }}>{title} <span className="text-ink/40 font-semibold text-[12px]">· {sub}</span></div>
      <ul className="mt-2 space-y-1.5">{items.map((t, i) => <li key={i} className="flex gap-2.5 text-[13px]"><span className="mt-0.5 inline-block h-3.5 w-3.5 rounded border-2 border-sand-400 shrink-0" />{t}</li>)}</ul>
    </div>
  );
  return (
    <Sheet className="p-[0.6in]">
      <div className="flex items-center justify-between border-b-2 pb-4" style={{ borderColor: RED }}>
        <Logo variant="red" className="h-8" /><div className="text-[15px] font-extrabold">Portfolio rollout checklist — {firm}</div>
      </div>
      <p className="mt-4 text-[12.5px] text-ink-600">A wave-by-wave playbook to roll Ask Redline out across the {firm} portfolio — from a first pilot to every community, with the ROI story to match.</p>
      <div className="mt-5">
        {phase("Portfolio setup", "Week 0", ["Pick 1–3 pilot communities to launch first", "Assign an internal rollout owner and a community-manager point of contact", "Centralize document sources (CC&Rs, rules, hours, fees, contacts) per community", "Confirm each community's phone number and opt-in keyword", "Set portfolio defaults: tone, allowed actions and branding", "Brief portfolio managers on the dashboard and unified inbox"])}
        {phase("Pilot launch", "Weeks 1–2", ["Go live in the pilot communities", "Print & post opt-in flyers, QR posters and welcome cards", "Send the launch email + SMS to residents", "Monitor conversations daily and fill gaps with Quick Facts", "Capture baseline wins: calls deflected, response time"])}
        {phase("Scale", "Weeks 3–8", ["Onboard the remaining communities in waves", "Reuse approved templates, tone and permissions", "Assign each community to its community manager (role-based access)", "Review portfolio analytics weekly", "Add the vendors and contacts residents actually ask for"])}
        {phase("Optimize & sell", "Days 30–90", ["Report portfolio ROI: volume handled, cost-to-serve, CSAT", "Feature the concierge in board renewals and new-business pitches", "Enable add-ons (voice, broadcasts, amenities) where they pay off", "Schedule a quarterly portfolio review with leadership"])}
      </div>
    </Sheet>
  );
}

// ── Portfolio ROI (management company) ───────────────────────────────────────
function RoiSheet({ name, communities, units, rate }) {
  const firm = firmOf(name);
  const nfmt = (n) => Math.round(n).toLocaleString("en-US");
  const usd = (n) => "$" + Math.round(n).toLocaleString("en-US");
  // Clearly-stated, deliberately conservative assumptions (shown on the sheet).
  const CONTACTS_PER_UNIT = 0.5; // resident calls/emails per unit, per month
  const DEFLECT = 0.6;           // share the concierge resolves without staff
  const MINUTES = 6;             // staff minutes saved per handled contact
  const monthlyContacts = units * CONTACTS_PER_UNIT;
  const deflected = monthlyContacts * DEFLECT;
  const hoursMo = (deflected * MINUTES) / 60;
  const laborYr = hoursMo * rate * 12;
  const fte = hoursMo / 173; // ~monthly full-time hours
  const perCommunityYr = communities ? laborYr / communities : 0;
  const stat = (big, small) => (
    <div className="rounded-2xl border border-sand-200 p-4 text-center">
      <div className="text-[24px] font-extrabold leading-none" style={{ color: RED, fontFamily: "Poppins, sans-serif" }}>{big}</div>
      <div className="mt-1.5 text-[11px] leading-snug text-ink-600">{small}</div>
    </div>
  );
  return (
    <Sheet className="p-[0.6in]">
      <div className="flex items-center justify-between border-b-2 pb-4" style={{ borderColor: RED }}>
        <Logo variant="red" className="h-9" />
        <div className="text-right text-[11px] uppercase tracking-[0.2em] font-bold" style={{ color: RED }}>Portfolio ROI estimate</div>
      </div>
      <h1 className="mt-7 text-[30px] leading-[1.1] font-extrabold" style={{ fontFamily: "Poppins, sans-serif" }}>
        What a concierge saves across<br />{nfmt(units)} doors.
      </h1>
      <p className="mt-3 text-[13px] text-ink-600 max-w-[6.2in]">
        A conservative estimate of the staff time and cost {firm} could take back by letting Ask Redline handle the
        repetitive resident contacts across the portfolio.
      </p>
      <div className="mt-4 flex flex-wrap gap-2 text-[11px]">
        {[[`${nfmt(communities)} communities`], [`${nfmt(units)} units / doors`], [`${usd(rate)} blended staff cost / hour`]].map(([c]) => (
          <span key={c} className="rounded-full bg-sand-100 px-3 py-1 font-semibold text-ink/70">{c}</span>
        ))}
      </div>

      <div className="mt-6 rounded-2xl p-6 text-white flex items-center justify-between" style={{ background: INK }}>
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-sand-300">Estimated staff cost avoided</div>
          <div className="mt-1 text-[46px] font-extrabold leading-none" style={{ fontFamily: "Poppins, sans-serif" }}>{usd(laborYr)}<span className="text-[18px] font-bold text-sand-300"> / year</span></div>
        </div>
        <div className="text-right text-[12px] text-sand-100/80">
          ≈ {usd(laborYr / 12)} every month<br />across your whole portfolio
        </div>
      </div>

      <div className="mt-5 grid grid-cols-4 gap-3">
        {stat(nfmt(monthlyContacts), "resident contacts a month, portfolio-wide")}
        {stat(nfmt(deflected), "handled by the concierge, no staff time")}
        {stat(`${nfmt(hoursMo)} hrs`, "staff hours saved every month")}
        {stat(fte.toFixed(1), "full-time employees' worth of work")}
      </div>

      <div className="mt-6 rounded-2xl border border-sand-200 p-5">
        <div className="text-[12px] font-extrabold text-ink">How we estimated this</div>
        <div className="mt-2 grid grid-cols-2 gap-x-6 gap-y-1.5 text-[11.5px] text-ink-600">
          <div>▸ <b>{CONTACTS_PER_UNIT}</b> resident calls/emails per unit each month</div>
          <div>▸ <b>{Math.round(DEFLECT * 100)}%</b> handled by the concierge without staff</div>
          <div>▸ <b>{MINUTES} minutes</b> of staff time saved per handled contact</div>
          <div>▸ <b>{usd(rate)}</b> blended, fully-loaded staff cost per hour</div>
          {communities ? <div>▸ ≈ <b>{usd(perCommunityYr)}/yr</b> saved per community</div> : null}
        </div>
      </div>
      <p className="mt-4 text-[10px] text-ink/45">
        Illustrative estimate for planning only — actual results vary by community size, call mix and staffing. Edit the
        door count, community count and staff cost in the kit to model your own portfolio.
      </p>
    </Sheet>
  );
}

// ── Community rollout signage ────────────────────────────────────────────────
function YardSign({ name, phone, link, keyword, voice = true }) {
  return (
    <Sheet className="p-[0.7in] min-h-[9.8in] flex flex-col items-center justify-center text-center text-white" style={{ background: RED }}>
      <Logo variant="white" className="h-10" />
      <div className="mt-8 text-[15px] uppercase tracking-[0.3em] font-bold text-white/85">Now live at {name}</div>
      <h1 className="mt-4 text-[54px] leading-[0.98] font-extrabold" style={{ fontFamily: "Poppins, sans-serif" }}>Your 24/7<br />resident concierge</h1>
      <p className="mt-5 text-[20px] text-white/90 max-w-[6in]">Rules, hours, gate codes, maintenance — just text{voice ? " or call" : ""} and get an answer in seconds.</p>
      <div className="mt-8 rounded-3xl bg-white p-5"><QRCodeSVG value={link} size={230} fgColor={INK} level="M" /></div>
      <div className="mt-6 text-[24px] font-extrabold">Scan, or text {keyword} to {phone}{voice ? " — or call it" : ""}</div>
      <div className="mt-2 text-[11px] text-white/70">Powered by Ask Redline · Msg &amp; data rates may apply · Reply STOP to opt out</div>
    </Sheet>
  );
}

function AmenitySign({ name, phone, link, keyword, voice = true }) {
  return (
    <Sheet className="p-[0.7in] min-h-[9.8in] flex flex-col items-center justify-center text-center">
      <Logo variant="red" className="h-9" />
      <div className="mt-8 text-[15px] uppercase tracking-[0.28em] font-bold" style={{ color: RED }}>Pool · Gym · Clubhouse</div>
      <h1 className="mt-3 text-[46px] leading-[1.02] font-extrabold" style={{ fontFamily: "Poppins, sans-serif" }}>Questions about<br />this amenity?</h1>
      <div className="mt-3 text-[24px] font-extrabold" style={{ color: RED }}>{voice ? "Text or call us." : "Just text us."}</div>
      <p className="mt-4 text-[16px] text-ink-600 max-w-[5.6in]">Check hours, reserve the space, report an issue or ask about the rules — any time, right from your phone.</p>
      <div className="mt-7 rounded-3xl border-4 p-5" style={{ borderColor: INK }}><QRCodeSVG value={link} size={220} fgColor={INK} level="M" /></div>
      <div className="mt-5 text-[18px]">Scan to opt in — or text <b>{keyword}</b> to <b>{phone}</b>{voice ? " — or just call it" : ""}</div>
      <div className="mt-2 text-[12px] text-ink/50">{name} · powered by Ask Redline</div>
    </Sheet>
  );
}

function WindowSign({ name, phone, link, keyword, voice = true }) {
  return (
    <Sheet className="p-[0.7in] min-h-[9.8in] flex flex-col items-center justify-center text-center text-white" style={{ background: INK }}>
      <Logo variant="white" className="h-9" />
      <h1 className="mt-8 text-[44px] leading-[1.03] font-extrabold" style={{ fontFamily: "Poppins, sans-serif" }}>Office closed?<br />We're still here.</h1>
      <p className="mt-4 text-[18px] text-sand-100/85 max-w-[5.6in]">{name}'s concierge answers by text{voice ? " or call" : ""}, day or night — no app, no login, no waiting for office hours.</p>
      <div className="mt-8 rounded-3xl bg-white p-5"><QRCodeSVG value={link} size={210} fgColor={INK} level="M" /></div>
      <div className="mt-6 text-[22px] font-extrabold">Text {keyword} to {phone}{voice ? " · or call it" : ""}</div>
      <div className="mt-2 text-[11px] text-white/60">Powered by Ask Redline · Reply STOP to opt out</div>
    </Sheet>
  );
}

function TableTent({ name, phone, link, keyword, voice = true }) {
  const panel = (flip) => (
    <div className={`flex-1 flex flex-col items-center justify-center text-center px-8 py-6 ${flip ? "rotate-180" : ""}`}>
      <Logo variant="red" className="h-7" />
      <h2 className="mt-3 text-[30px] leading-[1.05] font-extrabold" style={{ fontFamily: "Poppins, sans-serif" }}>Need something?<br />{voice ? "Text or call us." : "Just text us."}</h2>
      <p className="mt-2 text-[13px] text-ink-600 max-w-[4.6in]">{name}'s 24/7 concierge — rules, hours, bookings and maintenance.</p>
      <div className="mt-4 flex items-center gap-4">
        <div className="rounded-xl border-4 p-2" style={{ borderColor: INK }}><QRCodeSVG value={link} size={104} fgColor={INK} level="M" /></div>
        <div className="text-left text-[15px] font-extrabold leading-tight">Scan or text<br />{keyword} to {phone}{voice ? <><br />or call it</> : null}</div>
      </div>
    </div>
  );
  return (
    <Sheet className="p-[0.5in]">
      <div className="text-[11px] text-ink/40 mb-2 print:hidden">Prints one table tent — fold along the center dashed line so both faces stand upright.</div>
      <div className="flex flex-col rounded-2xl border-2 border-sand-200 overflow-hidden" style={{ minHeight: "9in" }}>
        {panel(true)}
        <div className="relative border-t-2 border-dashed border-sand-400">
          <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white px-2 text-[9px] uppercase tracking-widest text-ink/40 print:hidden">fold here</span>
        </div>
        {panel(false)}
      </div>
    </Sheet>
  );
}

function DoorHanger({ name, phone, link, keyword, voice = true }) {
  const one = (i) => (
    <div key={i} className="relative flex flex-col items-center rounded-2xl border-2 border-dashed border-sand-300 px-5 pt-3 pb-6 text-center" style={{ width: "3.5in", minHeight: "9.3in" }}>
      <div className="mt-1 h-9 w-9 rounded-full border-2 border-sand-300" />
      <div className="mt-1 h-3 w-1 bg-sand-300" />
      <Logo variant="red" className="mt-4 h-7" />
      <div className="mt-5 text-[12px] uppercase tracking-[0.2em] font-bold" style={{ color: RED }}>New at {name}</div>
      <h2 className="mt-2 text-[25px] leading-[1.05] font-extrabold" style={{ fontFamily: "Poppins, sans-serif" }}>Your community,<br />{voice ? "one call or text away." : "one text away."}</h2>
      <p className="mt-3 text-[13px] text-ink-600">Ask about rules, hours, gate codes, dues and maintenance — 24/7.</p>
      <div className="mt-5 rounded-2xl border-4 p-3" style={{ borderColor: INK }}><QRCodeSVG value={link} size={148} fgColor={INK} level="M" /></div>
      <div className="mt-4 text-[15px] font-extrabold">Text {keyword} to {phone}{voice ? " · or call it" : ""}</div>
      <div className="mt-auto pt-4 text-[9.5px] text-ink/45">Msg &amp; data rates may apply. Reply STOP to opt out.</div>
    </div>
  );
  return (
    <Sheet className="p-[0.5in]">
      <div className="text-[11px] text-ink/40 mb-2 print:hidden">Prints two door hangers — cut out and punch the top circle for the doorknob.</div>
      <div className="flex justify-center gap-4">{[0, 1].map(one)}</div>
    </Sheet>
  );
}

export default function KitPrint() {
  const [params] = useSearchParams();
  const asset = params.get("asset") || "onepager";
  const persona = ["company", "rollout"].includes(params.get("persona")) ? params.get("persona") : "board";
  const num = (v, d) => { const n = parseFloat(String(v || "").replace(/[^0-9.]/g, "")); return isFinite(n) && n > 0 ? n : d; };
  const props = {
    name: params.get("name") || "your community",
    phone: params.get("phone") || "(555) 123-4567",
    link: params.get("link") || "https://askredline.com/opt-in",
    keyword: (params.get("keyword") || "JOIN").toUpperCase(),
    voice: params.get("voice") !== "0",
  };
  const base = {
    onepager: <OnePager {...props} />, deck: <Deck {...props} />, flyer: <Flyer {...props} />,
    poster: <Poster {...props} />, card: <WelcomeCard {...props} />, announcement: <Announcement {...props} />,
    checklist: <Checklist {...props} />, faq: <Faq {...props} />, tech: <TechSheet />,
    yardsign: <YardSign {...props} />, amenitysign: <AmenitySign {...props} />, windowsign: <WindowSign {...props} />,
    tabletent: <TableTent {...props} />, doorhanger: <DoorHanger {...props} />,
  };
  const company = {
    onepager: <OnePagerCompany {...props} />, deck: <DeckCompany {...props} />,
    checklist: <ChecklistCompany {...props} />, faq: <FaqCompany {...props} />,
    roi: <RoiSheet {...props} communities={num(params.get("communities"), 10)} units={num(params.get("units"), 2500)} rate={num(params.get("rate"), 45)} />,
  };
  const render = (persona === "company" && company[asset]) || base[asset] || <OnePager {...props} />;
  const title = (persona === "company" && ASSET_TITLES_COMPANY[asset]) || ASSET_TITLES[asset] || "Asset";

  return (
    <div className="min-h-screen bg-sand-200 py-6 print:bg-white print:py-0">
      <style>{`
        @page { size: letter; margin: 0; }
        @media print {
          .no-print, .no-print * { display: none !important; }
          .sheet {
            box-shadow: none !important; border-radius: 0 !important; margin: 0 !important;
            width: 8.5in !important; min-height: 11in !important;
          }
          .sheet:last-child { break-after: auto !important; page-break-after: auto !important; }
          html, body { background: #fff !important; }
        }
        * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
        .sheet { width: 8.0in; box-shadow: 0 10px 40px rgba(0,0,0,.12); border-radius: 12px; }
        .break-after-page { break-after: page; page-break-after: always; }
      `}</style>

      <div className="no-print sticky top-0 z-10 mx-auto mb-6 flex max-w-[8in] items-center justify-between rounded-full border border-sand-300 bg-white/90 px-4 py-2.5 backdrop-blur">
        <Link to="/kit" className="flex items-center gap-1.5 text-sm font-semibold text-ink hover:text-redline-600">
          <ArrowLeft className="h-4 w-4" /> Back to kit
        </Link>
        <div className="text-sm font-bold text-ink">{title}</div>
        <button onClick={() => window.print()} data-testid="kit-print-btn"
          className="flex items-center gap-1.5 rounded-full bg-redline-500 px-4 py-2 text-sm font-bold text-white hover:bg-redline-600 transition-colors">
          <Printer className="h-4 w-4" /> Print / Save as PDF
        </button>
      </div>

      <div className="space-y-6 print:space-y-0">{render}</div>
    </div>
  );
}
